import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useCreatePost } from '@/hooks/usePosts';
import { useSavePostMedia, UploadedMedia } from '@/hooks/useMediaUpload';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { useMembershipContext } from '@/components/layout/AppLayout';
import { useCreateMentionNotifications } from '@/hooks/useNotifications';
import { MediaUploader } from './MediaUploader';
import { MentionInput } from './MentionInput';
import { Send, Briefcase, MessageSquare, Megaphone, ImagePlus } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type PostCategory = Database['public']['Enums']['post_category'];

interface PostComposerProps {
  cohortTag: string;
  channelId?: string;
}

const CATEGORIES: { value: PostCategory; label: string; icon: React.ElementType; description: string }[] = [
  { value: 'referral', label: 'Opportunity', icon: Briefcase, description: 'Job, partnership, or intro' },
  { value: 'discussion', label: 'Discussion', icon: MessageSquare, description: 'Advice or resource sharing' },
  { value: 'plug', label: 'Shameless Plug', icon: Megaphone, description: 'Self-promotion' },
];

export function PostComposer({ cohortTag, channelId }: PostComposerProps) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<PostCategory>('discussion');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMediaUploader, setShowMediaUploader] = useState(false);
  const [media, setMedia] = useState<UploadedMedia[]>([]);

  const { data: profile } = useProfile();
  const { isPending } = useMembershipContext();
  const createPost = useCreatePost();
  const savePostMedia = useSavePostMedia();
  const createMentionNotifications = useCreateMentionNotifications();
  const { toast } = useToast();

  if (isPending) {
    return (
      <Card className="card-qbn">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground text-center">
            Complete your profile to start posting.
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleSubmit = async () => {
    if (!content.trim() && media.length === 0) return;

    try {
      // Create the post
      const post = await createPost.mutateAsync({
        content: content.trim(),
        category,
        cohort_tag: cohortTag,
        channel_id: channelId,
      });

      // Save media if any
      if (media.length > 0) {
        await savePostMedia.mutateAsync({
          postId: post.id,
          media,
        });
      }

      // Send mention notifications
      if (content.includes('@')) {
        createMentionNotifications.mutate({
          content: content.trim(),
          postId: post.id,
        });
      }

      // Reset form
      setContent('');
      setMedia([]);
      setIsExpanded(false);
      setShowMediaUploader(false);

      toast({
        title: 'Posted!',
        description: 'Your post has been shared with the community.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const isSubmitting = createPost.isPending || savePostMedia.isPending;
  const canSubmit = (content.trim() || media.length > 0) && !isSubmitting;

  return (
    <Card className="card-qbn">
      <CardContent className="p-4">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10">
            <SignedAvatarImage src={profile?.avatar_url || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {profile?.full_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-3">
            <MentionInput
              placeholder="Share an opportunity, ask for advice, or start a discussion... Use @ to mention"
              value={content}
              onChange={setContent}
              onFocus={() => setIsExpanded(true)}
              className="min-h-[80px] resize-none border-0 bg-muted/50 focus-visible:ring-1"
            />

            {isExpanded && (
              <div className="space-y-3 animate-fade-in">
                {/* Media Uploader */}
                {showMediaUploader && (
                  <MediaUploader
                    media={media}
                    onMediaChange={setMedia}
                    maxFiles={4}
                  />
                )}

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Select value={category} onValueChange={(v) => setCategory(v as PostCategory)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <div className="flex items-center gap-2">
                              <cat.icon className="h-4 w-4" />
                              <span>{cat.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {!showMediaUploader && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowMediaUploader(true)}
                        className="shrink-0"
                      >
                        <ImagePlus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="btn-qbn-primary"
                  >
                    {isSubmitting ? 'Posting...' : 'Post'}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
